import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Image, Input, Select } from 'antd';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import { useNavigate } from 'react-router-dom';
import {
  createLearningAsset,
  findLearningAssetById,
  updateLearningAsset,
} from '../../../../service/learningAssetService';
import { Category, LearningBook, Tag } from '../../../../model/response';
import { findAllTags } from '../../../../service/tagService';
import { findAllCategories } from '../../../../service/categoryService';
import { LearningAssetCreateRequest, LearningAssetStatus, LearningAssetUpdateRequest } from '../../../../model/request';
import toast, { NotifyType } from '../../../../utils/toast';
import { useFilePicker } from 'use-file-picker';
import { FileAmountLimitValidator, FileSizeValidator, FileTypeValidator } from 'use-file-picker/validators';
import { findAllLearningBooks } from '../../../../service/learningBookService';
import HeaderAction from '../HeaderAction';
import { learningAssetStatusList } from '../../../../utils/enumMapper';
import processError from '../../../../utils/processError';

function LearningAssetForm() {
  const { id } = useParams();
  const filePicker = useFilePicker({
    multiple: false,
  });
  const thumbnailPicker = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false,
    validators: [
      new FileAmountLimitValidator({ max: 1 }),
      new FileTypeValidator(['jpg', 'png']),
      new FileSizeValidator({ maxFileSize: 50 * 1024 * 1024 }),
    ],
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chapter, setChapter] = useState('');
  const [lesson, setLesson] = useState('');
  const [status, setStatus] = useState<LearningAssetStatus | null>('ACTIVE');
  const [learningBooks, setLearningBooks] = useState<LearningBook[]>([]);
  const [learningBookFetching, setLearningBookFetching] = useState(false);
  const [learningBookId, setLearningBookId] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagFetching, setTagFetching] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryFetching, setCategoryFetching] = useState(false);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [savedFilePath, setSavedFilePath] = useState<string | null>(null);
  const [savedOriginFilename, setSavedOriginFilename] = useState<string | null>(null);
  const [savedThumbnailPath, setSavedThumbnailPath] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTags();
    fetchCategories();
    fetchLearningBooks();
  }, []);

  useEffect(() => {
    if (id) {
      findLearningAssetById(id)
        .then((learningAsset) => {
          setName(learningAsset.name);
          setDescription(learningAsset.description || '');
          setChapter(learningAsset.chapter || '');
          setLesson(learningAsset.lesson || '');
          setStatus(learningAsset.status ?? null);
          setTagIds(learningAsset.tags.map((tag) => tag.id));
          setCategoryIds(learningAsset.categories.map((category) => category.id));
          setSavedFilePath(learningAsset.filePath);
          setSavedOriginFilename(learningAsset.originFilename);
          setSavedThumbnailPath(learningAsset.thumbnailPath ?? null);
          setLearningBookId(learningAsset.learningBook?.id ?? null);
        })
        .catch((e) => console.error(e));
    }
  }, [id]);

  function fetchLearningBooks() {
    if (learningBookFetching) return;
    setLearningBookFetching(true);
    findAllLearningBooks()
      .then((tags) => setLearningBooks(tags))
      .catch((e) => console.error(e))
      .finally(() => setLearningBookFetching(false));
  }

  function fetchTags() {
    if (tagFetching) return;
    setTagFetching(true);
    findAllTags()
      .then((tags) => setTags(tags))
      .catch((e) => console.error(e))
      .finally(() => setTagFetching(false));
  }

  function fetchCategories() {
    if (categoryFetching) return;
    setCategoryFetching(true);
    findAllCategories()
      .then((categories) => setCategories(categories))
      .catch((e) => console.error(e))
      .finally(() => setCategoryFetching(false));
  }

  function handleGoBack() {
    navigate('/learning-assets');
  }

  async function handleSubmit() {
    if (!learningBookId) {
      toast(NotifyType.ERROR, 'Vui lòng chọn sách');
      return;
    }
    try {
      if (id) {
        const learningAssetUpdateRequest: LearningAssetUpdateRequest = {
          name,
          description,
          chapter,
          lesson,
          status: status || undefined,
          learningBook: { id: learningBookId },
          categories: categoryIds.map((categoryId) => ({ id: categoryId })),
          tags: tagIds.map((tagId) => ({ id: tagId })),
          file: filePicker.plainFiles && filePicker.plainFiles.length > 0 ? filePicker.plainFiles[0] : undefined,
          originFilename: filePicker.filesContent[0]?.name,
          thumbnail:
            thumbnailPicker.plainFiles && thumbnailPicker.plainFiles.length > 0
              ? thumbnailPicker.plainFiles[0]
              : undefined,
        };
        await updateLearningAsset(id, learningAssetUpdateRequest);
        toast(NotifyType.INFO, 'Cập nhật học liệu số thành công');
        navigate('/learning-assets');
        return;
      }
      if (!filePicker.plainFiles || !filePicker.plainFiles.length) {
        toast(NotifyType.ERROR, 'Vui lòng chọn tệp học liệu số');
        return;
      }
      const learningAssetCreateRequest: LearningAssetCreateRequest = {
        name,
        description,
        learningBook: { id: learningBookId },
        categories: categoryIds.map((categoryId) => ({ id: categoryId })),
        tags: tagIds.map((tagId) => ({ id: tagId })),
        file: filePicker.plainFiles[0],
        originFilename: filePicker.filesContent[0].name,
        thumbnail: thumbnailPicker.plainFiles[0],
      };
      await createLearningAsset(learningAssetCreateRequest);
      toast(NotifyType.INFO, 'Tạo học liệu số thành công');
      navigate('/learning-assets');
    } catch (err) {
      processError(err);
    }
  }

  return (
    <div className={'w-full h-full bg-white flex flex-col'}>
      <HeaderAction handleGoBack={handleGoBack} handleSubmit={handleSubmit} />
      <div className={'w-full p-8 h-[calc(100vh-160px)] overflow-y-auto'}>
        <h2 className={'font-bold text-xl'}>{id ? 'Chỉnh sửa học liệu số' : 'Thêm học liệu số mới'}</h2>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Tên học liệu số:</div>
          <Input className="flex-1" name={'name'} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Mô tả chi tiết:</div>
          <TextArea
            className="flex-1"
            name={'description'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
          />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Chương:</div>
          <Input className="flex-1" name={'chapter'} value={chapter} onChange={(e) => setChapter(e.target.value)} />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Bài:</div>
          <Input className="flex-1" name={'chapter'} value={lesson} onChange={(e) => setLesson(e.target.value)} />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Trạng thái:</div>
          <Select
            onChange={(value) => setStatus(value)}
            className="w-full"
            placeholder="Trạng thái"
            style={{ flex: 1 }}
            value={status}
            options={learningAssetStatusList}
          />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Sách:</div>
          <Select
            onChange={setLearningBookId}
            placeholder="Chọn Sách"
            style={{ flex: 1 }}
            value={learningBookId}
            options={learningBooks.map((learningBook) => ({
              label: learningBook.name,
              value: learningBook.id,
            }))}
            showSearch={true}
            filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
          />
          <Button className={'ml-2'} onClick={fetchLearningBooks}>
            {learningBookFetching ? <LoadingOutlined /> : <ReloadOutlined />}
          </Button>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Thẻ:</div>
          <Select
            mode="multiple"
            onChange={setTagIds}
            placeholder="Chọn thẻ"
            style={{ flex: 1 }}
            value={tagIds}
            options={tags.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
          />
          <Button className={'ml-2'} onClick={fetchTags}>
            {tagFetching ? <LoadingOutlined /> : <ReloadOutlined />}
          </Button>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Chủ đề:</div>
          <Select
            mode="multiple"
            onChange={setCategoryIds}
            placeholder="Chọn chủ đề"
            style={{ flex: 1 }}
            value={categoryIds}
            options={categories.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
          />
          <Button className={'ml-2'} onClick={fetchCategories}>
            {categoryFetching ? <LoadingOutlined /> : <ReloadOutlined />}
          </Button>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Tệp học liệu số:</div>
          <div className={'flex gap-x-4 items-center'}>
            <Button onClick={() => filePicker.openFilePicker()}>
              {(filePicker.filesContent && filePicker.filesContent.length > 0) || (id && savedFilePath)
                ? 'Thay đổi tệp'
                : 'Chọn tệp'}
            </Button>
            {filePicker.filesContent && filePicker.filesContent.length > 0 ? (
              <div>
                <h2>{filePicker.filesContent[0].name}</h2>
              </div>
            ) : id && savedFilePath ? (
              <div>
                <h2>{savedOriginFilename}</h2>
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Thumbnail:</div>
          <div className={'flex flex-col gap-y-2 justify-start'}>
            <div className={'flex gap-x-2'}>
              <Button className={'w-fit'} onClick={() => thumbnailPicker.openFilePicker()}>
                {(thumbnailPicker.filesContent && thumbnailPicker.filesContent.length > 0) || (id && savedThumbnailPath)
                  ? 'Thay đổi thumbnail'
                  : 'Chọn thumbnail'}
              </Button>
            </div>
            {thumbnailPicker.filesContent && thumbnailPicker.filesContent.length > 0 ? (
              <div>
                <img
                  className={'h-[180px] w-auto rounded border-1'}
                  alt={thumbnailPicker.filesContent[0].name}
                  src={thumbnailPicker.filesContent[0].content}
                ></img>
              </div>
            ) : id && savedThumbnailPath ? (
              <div>
                <Image className={'rounded border-1'} width={'auto'} height={180} alt={id} src={savedThumbnailPath} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LearningAssetForm;
